import { internalFetch, postCsv } from '../utils/fetchHelper';
import type { IImportCsvRow } from '../utils/csvHelpers';
import { IPagedCollection } from '@moko/utils';
import { IGroupInUser } from './groupService';

export enum UserRoleType {
  FMSManager = 0,
  FMSClient = 1,
  FMSContractor = 2,
  FMSPortalAdmin = 3,
}

export interface IUserQualification {
  id?: number;
  userId?: number;
  certificationTypeId: number;
  sourceSystemCode: string;
  externalCredentialId: string;
  isEligible: boolean;
  validUntil?: string | null;
  lastSyncedAt: string;
}

export type IUserQualificationWrite = {
  certificationTypeId: number;
  sourceSystemCode: string;
  externalCredentialId: string;
  isEligible: boolean;
  validUntil: string | null;
  lastSyncedAt: string;
};

export const toQualificationWrite = (
  qualification: IUserQualification
): IUserQualificationWrite => ({
  certificationTypeId: qualification.certificationTypeId,
  sourceSystemCode: qualification.sourceSystemCode,
  externalCredentialId: qualification.externalCredentialId,
  isEligible: qualification.isEligible === true,
  validUntil: qualification.validUntil ?? null,
  lastSyncedAt: qualification.lastSyncedAt,
});

export interface IUser {
  id: number;
  username: string;
  firstName?: string;
  lastName?: string;
  email: string;
  groups?: IGroupInUser[];
  defaultTenant?: number;
  createdAt?: string;
  otherInfo?: string;
  roles?: string[];
  defaultRole?: string;
  qualifications?: IUserQualification[];
}

export interface IUserRefValue {
  id: number;
  key: string;
  firstName?: string;
  lastName?: string;
  email: string;
}

export interface ICreateUserRequest {
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  groupsId: number[];
  defaultTenantId: number;
  otherInfo?: string;
  provisionIdentity?: boolean;
  identityPassword?: string;
  isExternalUser?: boolean;
  roles?: UserRoleType[];
  defaultRole?: UserRoleType;
  qualifications?: IUserQualificationWrite[];
}

export interface ICreateUserResponse {
  userId: number;
}

export interface IUpdateUserRequest {
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  groupsId: number[];
  defaultTenantId: number;
  otherInfo?: string;
  roles?: string[];
  defaultRole?: string;
  qualifications?: IUserQualificationWrite[];
}

export const getUsers = async (
  abortController: AbortController,
  pageSize: number,
  pageNo: number,
  orderBy?: string,
  search?: string
): Promise<IPagedCollection<IUser>> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/a/users?pageSize=${pageSize}&pageNo=${pageNo}${
      orderBy ? `&orderBy=${orderBy}` : ''
    }${search ? `&search=${search}` : ''}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'GET',
    abortController
  );
};

export const getUsersRefValues = async (
  abortController: AbortController,
  pageSize: number,
  pageNo: number,
  orderBy?: string,
  search?: string
): Promise<IPagedCollection<IUserRefValue>> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/userSelectorValues?pageSize=${pageSize}&pageNo=${pageNo}${
      orderBy ? `&orderBy=${orderBy}` : ''
    }${search ? `&search=${search}` : ''}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'GET',
    abortController
  );
};

/**
 * Resolve user/group display names by their keys (e.g. "G9", "U9").
 *
 * Bug 490: the backend expects multiple ids to be separated by "|", not ",".
 * A comma separator returns a 500 FormatException, so we join with "|" and
 * URL-encode the value.
 *
 * Accepts an array of keys (preferred) or a string for back-compat.
 */
export const getUserOrGroupsNames = async (
  abortController: AbortController,
  ids?: string | string[]
): Promise<Record<string, string>> => {
  const idList = (Array.isArray(ids) ? ids : (ids ?? '').split(/[,|]/))
    .map((id) => id.trim())
    .filter(Boolean);

  if (idList.length === 0) return {};

  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/a/usersOrGroupsNames?ids=${encodeURIComponent(idList.join('|'))}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'GET',
    abortController
  );
};

export const getUserLabels = async (
  abortController: AbortController,
  username: string
): Promise<any> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/userLabels?username=${username}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'GET',
    abortController
  );
};

export const getUser = async (
  abortController: AbortController,
  userId: number
): Promise<IUser> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/a/user?userId=${userId}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'GET',
    abortController
  );
};

export const editUser = async (
  abortController: AbortController,
  userId: number,
  user: IUpdateUserRequest
): Promise<number> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/a/user?userId=${userId}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'PUT',
    abortController,
    JSON.stringify(user)
  );
};

export const deleteUser = async (
  abortController: AbortController,
  userId: number
): Promise<number> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/a/user?userId=${userId}`,
    'DELETE',
    abortController
  );
};

export const createUser = async (
  abortController: AbortController,
  user: ICreateUserRequest
): Promise<ICreateUserResponse> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/a/user`,
    'POST',
    abortController,
    JSON.stringify(user)
  );
};

export interface IImportUsersResponse {
  importUserResponses: IImportCsvRow[];
  messages: string[];
  errorsCount: number;
}

export const importUsers = (
  abortController: AbortController,
  csvContent: string
): Promise<IImportUsersResponse> =>
  postCsv<IImportUsersResponse>(
    `${import.meta.env.VITE_BACKEND_API_URL}/a/importUsers`,
    csvContent,
    abortController
  );

export const exportUsers = async (
  abortController: AbortController
): Promise<string> => {
  const response: Response = await internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/a/exportUsers?code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'GET',
    abortController,
    undefined,
    true
  );

  return response.text();
};
